"use client"
import {
  METRIC_LABEL_BASELINE_TRIM_CLASS,
  METRIC_TRIGGER_RED_CHIP_STYLE,
  useMetricHoverSwap,
  type HoverableMetricId,
} from "../hud/blocks/metrics/metricHoverSwap"
import { useMetricHoverTrigger } from "../hud/blocks/hoverSwapStickyZone"
import { useCurrentCapabilityStats } from "../hud/blocks/capability/useCurrentCapabilityStats"

// ─── Editable content ────────────────────────────────────────────────────────
// The tab-74 stat-row pair: the combined graph hover's two live numbers
// (R&D speedup, current-level takeover risk) promoted to the HUD's data bar,
// where Safety Researchers / Total Slowdown normally sit. Values come from
// useCurrentCapabilityStats (the story date's interpolated capability
// value); hovering shows the definition in the below-HUD slot like the
// other stats (METRIC_HOVER_DEFINITIONS "rdSpeedup" / "takeoverRisk",
// metricHoverSwap.tsx).
export const CAPABILITY_RISK_STATS_COPY = {
  rdSpeedupLabel: "R&D Speedup",
  takeoverRiskLabel: "Takeover Risk",
}

// One stat-row figure in the row's valueFirst voice (bold 11px value over
// the small label), reporting hover to the metric swap. Unlike the four
// stock stats there's no MetricTrigger (no details panel to open), so the
// red-chip treatment — reserved padding, chip lit while hovered — is
// painted here with the same METRIC_TRIGGER_RED_CHIP_STYLE knobs.
const CapabilityRiskStat = ({
  metricId,
  label,
  valueText,
  labelFontSize = "8.5px",
}: {
  metricId: HoverableMetricId
  label: string
  valueText: string
  labelFontSize?: string
}) => {
  const metricSwap = useMetricHoverSwap()
  const hoverProps = useMetricHoverTrigger(metricId, valueText)
  const chipTreatment = metricSwap?.triggerHighlight === "red-chip"
  const chipLit = chipTreatment && metricSwap?.hoveredMetric?.id === metricId
  const chip = METRIC_TRIGGER_RED_CHIP_STYLE
  return (
    <div className={`flex ${chipTreatment ? "items-stretch" : "items-center"}`}>
      <div
        className="flex flex-col-reverse justify-end items-center w-min"
        {...hoverProps}
        style={{
          ...(chipTreatment && {
            padding: `${chip.paddingYPx}px ${chip.paddingXPx}px`,
            marginLeft: -chip.paddingXPx,
            marginRight: -chip.paddingXPx,
            borderRadius: chip.borderRadiusPx,
          }),
          ...(chipLit && {
            background: chip.background,
            color: chip.textColor,
          }),
        }}
      >
        <div
          className={`text-center ${metricSwap ? METRIC_LABEL_BASELINE_TRIM_CLASS : ""}`}
          style={{ fontSize: labelFontSize }}
        >
          {label}
        </div>
        <div className="text-[11px] font-bold whitespace-nowrap">{valueText}</div>
      </div>
    </div>
  )
}

export const RdSpeedupStat = ({ labelFontSize }: { labelFontSize?: string }) => {
  const stats = useCurrentCapabilityStats()
  if (!stats) return null
  return (
    <CapabilityRiskStat
      metricId="rdSpeedup"
      label={CAPABILITY_RISK_STATS_COPY.rdSpeedupLabel}
      valueText={stats.speedupText}
      labelFontSize={labelFontSize}
    />
  )
}

export const TakeoverRiskStat = ({ labelFontSize }: { labelFontSize?: string }) => {
  const stats = useCurrentCapabilityStats()
  if (!stats?.riskText) return null
  return (
    <CapabilityRiskStat
      metricId="takeoverRisk"
      label={CAPABILITY_RISK_STATS_COPY.takeoverRiskLabel}
      valueText={stats.riskText}
      labelFontSize={labelFontSize}
    />
  )
}
